import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useModuleApi } from '@/features/menu-management/composables/useModuleApi';
import { useUserRoutes } from '@/common/composables/useUserRoutes';
import { useLocale } from '@/common/composables/useLocale';
import { NavigationService } from '@/features/menu-management/utils/navigationUtils';
import type {
  NavigationModuleWithLinks,
  MenuLinkModel,
} from '@/features/menu-management/types/menuTypes';
import { LinkTypeEnum } from '@/features/menu-management/constants/menuConstants';

export interface SearchResult {
  id: string;
  title: string;
  description?: string;
  category?: string;
  type: LinkTypeEnum | 'route';
  icon?: string;
  path?: string;
  action?: () => void;
  keywords?: string[];
  url?: string;
}

interface UseQuickSearchOptions {
  onNavigate?: (result: SearchResult) => void;
  enableKeyboardShortcut?: boolean;
}

/**
 * Composable pour la logique de recherche rapide
 * Récupère automatiquement les données via useModuleApi et useUserRoutes
 * Utilisé par ZenQuickSearch (desktop) et ZenQuickSearchMobile (mobile-web)
 */
export function useQuickSearch(options: UseQuickSearchOptions = {}) {
  const router = useRouter();
  const { modulesWithLinks, hasModules, loading: modulesLoading } = useModuleApi();
  const { displayRoutes, hasRoutes } = useUserRoutes();
  const { currentLocale, t } = useLocale();

  // Reactive state
  const searchQuery = ref('');
  const isFocused = ref(false);
  const highlightedIndex = ref(0);
  const searchInputRef = ref<HTMLInputElement>();

  // Data loading state
  const isDataLoaded = computed(() => {
    // La recherche est activée uniquement si les modules ET les routes sont chargés
    return hasModules.value && hasRoutes.value && !modulesLoading.value;
  });

  // ============================================
  // Helpers for localization
  // ============================================

  function getLocalizedModuleName(module: NavigationModuleWithLinks): string {
    const locale = currentLocale.value;
    if (module.nameTranslations && module.nameTranslations.length > 0) {
      const translation = module.nameTranslations.find((trans) =>
        trans.culture.toLowerCase().startsWith(locale.toLowerCase())
      );
      if (translation) {
        return translation.value;
      }
    }
    return module.name;
  }

  function getLocalizedLinkName(link: MenuLinkModel): string {
    const locale = currentLocale.value;
    if (link.nameTranslations && link.nameTranslations.length > 0) {
      const translation = link.nameTranslations.find((trans) =>
        trans.culture.toLowerCase().startsWith(locale.toLowerCase())
      );
      if (translation) {
        return translation.value;
      }
    }
    return link.name;
  }

  // ============================================
  // Search Index
  // ============================================

  // Build searchable index
  const searchIndex = computed<SearchResult[]>(() => {
    const results: SearchResult[] = [];

    // Add user routes
    displayRoutes.value.forEach((route) => {
      const meta = route.meta as Record<string, unknown> | undefined;
      const titleKey = meta?.title as string | undefined;
      const translatedTitle = titleKey ? t(titleKey) : String(route.name);
      results.push({
        id: `route-${String(route.name)}`,
        title: translatedTitle,
        description: meta?.description as string | undefined,
        category: 'Navigation',
        type: 'route',
        icon: meta?.icon as string | undefined,
        path: route.path,
        keywords: (meta?.keywords as string[]) || [],
      });
    });

    // Add links from modules
    modulesWithLinks.value.forEach((module) => {
      const moduleName = getLocalizedModuleName(module);
      module.links?.forEach((link) => {
        const linkName = getLocalizedLinkName(link);
        results.push({
          id: `link-${link.id}`,
          title: linkName,
          description: getLinkDescription(link),
          category: moduleName,
          type: link.linkType,
          url: link.url,
          path: link.linkType === LinkTypeEnum.Ward ? link.url : undefined,
          action: () => handleLinkNavigation(link),
          keywords: generateKeywords(linkName, moduleName),
        });
      });
    });

    return results;
  });

  // Filtered results based on search query
  const filteredResults = computed(() => {
    if (!searchQuery.value.trim()) return [];

    const query = searchQuery.value.toLowerCase().trim();
    const queryTerms = query.split(/\s+/);

    return searchIndex.value
      .filter((result) => {
        const searchableText = [
          result.title,
          result.description,
          result.category,
          ...(result.keywords || []),
        ]
          .join(' ')
          .toLowerCase();

        // Must match all terms
        return queryTerms.every((term) => searchableText.includes(term));
      })
      .sort((a, b) => {
        // Prioritize exact matches in title
        const aTitle = a.title.toLowerCase();
        const bTitle = b.title.toLowerCase();

        if (aTitle.includes(query) && !bTitle.includes(query)) return -1;
        if (!aTitle.includes(query) && bTitle.includes(query)) return 1;

        // Then by type (routes first)
        if (a.type !== b.type) {
          return a.type === LinkTypeEnum.Ward ? -1 : 1;
        }

        return a.title.localeCompare(b.title);
      })
      .slice(0, 8); // Limit results
  });

  function getLinkDescription(link: MenuLinkModel): string {
    const descriptions: Record<string, string> = {
      UPWARD: "Plateforme d'apprentissage en ligne",
      Agenda: 'Consultez votre emploi du temps',
      Absences: 'Gérez vos absences',
      'Office 365': 'Suite bureautique Microsoft',
    };
    return descriptions[link.name] || '';
  }

  function generateKeywords(title: string, category: string): string[] {
    const keywords = [title, category];

    // Add common synonyms
    const synonyms: Record<string, string[]> = {
      académique: ['cours', 'étude', 'école', 'université'],
      professionnelle: ['stage', 'emploi', 'carrière', 'travail'],
      administratif: ['papier', 'document', 'inscription'],
      pratique: ['quotidien', 'vie', 'utile'],
    };

    Object.entries(synonyms).forEach(([key, values]) => {
      if (category.toLowerCase().includes(key)) {
        keywords.push(...values);
      }
    });

    return keywords;
  }

  function highlightMatch(text: string): string {
    if (!searchQuery.value.trim()) return text;

    const query = searchQuery.value.trim();
    const regex = new RegExp(`(${query})`, 'gi');

    return text.replace(regex, '<mark>$1</mark>');
  }

  function handleFocus() {
    isFocused.value = true;
    highlightedIndex.value = 0;
  }

  function handleBlur() {
    // Delay to allow click events on results
    setTimeout(() => {
      isFocused.value = false;
    }, 200);
  }

  function handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        highlightedIndex.value = Math.min(
          highlightedIndex.value + 1,
          filteredResults.value.length - 1
        );
        break;

      case 'ArrowUp':
        event.preventDefault();
        highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
        break;

      case 'Enter':
        event.preventDefault();
        if (filteredResults.value[highlightedIndex.value]) {
          selectResult(filteredResults.value[highlightedIndex.value]);
        }
        break;

      case 'Escape':
        clearSearch();
        searchInputRef.value?.blur();
        break;

      default:
        // Number shortcuts (1-9)
        if (event.key >= '1' && event.key <= '9') {
          const index = parseInt(event.key) - 1;
          if (filteredResults.value[index]) {
            selectResult(filteredResults.value[index]);
          }
        }
    }
  }

  function handleLinkNavigation(link: MenuLinkModel) {
    const linkName = getLocalizedLinkName(link);
    if (options.onNavigate) {
      options.onNavigate({
        id: `link-${link.id}`,
        title: linkName,
        type: link.linkType,
      });
    }
    NavigationService.handleLinkNavigation({ url: link.url, type: link.linkType }, router);
  }

  function selectResult(result: SearchResult) {
    // If result has an action (link navigation), execute it
    if (result.action) {
      result.action();
    }
    // If result is a route, navigate to it
    else if (result.type === 'route' && result.path) {
      router.push(result.path);
    }
    // Fallback to emit navigation event
    else if (options.onNavigate) {
      options.onNavigate(result);
    }

    clearSearch();
    searchInputRef.value?.blur();
  }

  function clearSearch() {
    searchQuery.value = '';
    highlightedIndex.value = 0;
  }

  function closeSearch() {
    clearSearch();
    isFocused.value = false;
    searchInputRef.value?.blur();
  }

  function focusInput() {
    searchInputRef.value?.focus();
  }

  // Global keyboard shortcuts
  function handleGlobalKeydown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      searchInputRef.value?.focus();
    }
  }

  // Setup keyboard shortcut listener if enabled
  function setupKeyboardShortcut() {
    if (options.enableKeyboardShortcut !== false) {
      document.addEventListener('keydown', handleGlobalKeydown);
    }
  }

  function cleanupKeyboardShortcut() {
    if (options.enableKeyboardShortcut !== false) {
      document.removeEventListener('keydown', handleGlobalKeydown);
    }
  }

  return {
    // State
    searchQuery,
    isFocused,
    highlightedIndex,
    searchInputRef,

    // Data loading
    isDataLoaded,

    // Computed
    searchIndex,
    filteredResults,

    // Methods
    handleFocus,
    handleBlur,
    handleKeydown,
    selectResult,
    clearSearch,
    closeSearch,
    focusInput,
    highlightMatch,

    // Keyboard shortcut lifecycle
    setupKeyboardShortcut,
    cleanupKeyboardShortcut,
  };
}
